import { useEffect, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from '@/components/ui-v2';
import { iamApi, type InvitePreviewResult } from '../services/iam.api';
import { showSuccessToast, showErrorToast } from '@/services/toastService';

interface IamRole {
    _id: string;
    name: string;
    scope: string;
}

interface IamWorkspace {
    _id: string;
    name: string;
}

export interface InviteMembersModalProps {
    open: boolean;
    onClose: () => void;
    onSent: () => void;
    roles: IamRole[];
    workspaces: IamWorkspace[];
    currentWorkspaceId: string | null;
    isOrgAdmin: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function InviteMembersModal({
    open,
    onClose,
    onSent,
    roles,
    workspaces,
    currentWorkspaceId,
    isOrgAdmin,
}: InviteMembersModalProps) {
    const [emailDraft, setEmailDraft] = useState('');
    const [emails, setEmails] = useState<string[]>([]);
    const [asOrgAdmin, setAsOrgAdmin] = useState(false);
    const [workspaceId, setWorkspaceId] = useState<string>('');
    const [roleId, setRoleId] = useState<string>('');
    const [previewing, setPreviewing] = useState(false);
    const [preview, setPreview] = useState<InvitePreviewResult[] | null>(null);
    const [sending, setSending] = useState(false);

    const wsRoles = useMemo(() => roles.filter((r) => r.scope === 'workspace'), [roles]);
    const orgAdminRole = useMemo(() => roles.find((r) => r.scope === 'organization'), [roles]);

    useEffect(() => {
        if (!open) return;
        setEmailDraft('');
        setEmails([]);
        setAsOrgAdmin(false);
        setWorkspaceId(currentWorkspaceId || workspaces[0]?._id || '');
        setRoleId(wsRoles[0]?._id || '');
        setPreview(null);
    }, [open, currentWorkspaceId, workspaces, wsRoles]);

    const commitEmail = () => {
        const parts = emailDraft
            .split(/[\s,;]+/)
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean);
        const valid: string[] = [];
        let bad: string | null = null;
        for (const p of parts) {
            if (!EMAIL_REGEX.test(p)) {
                bad = p;
                continue;
            }
            if (!emails.includes(p) && !valid.includes(p)) valid.push(p);
        }
        if (bad) showErrorToast(`"${bad}" is not a valid email`);
        if (valid.length) setEmails((prev) => [...prev, ...valid]);
        setEmailDraft('');
        setPreview(null);
    };

    const removeEmail = (email: string) => {
        setEmails((prev) => prev.filter((e) => e !== email));
        setPreview(null);
    };

    const canSubmit =
        emails.length > 0 && (asOrgAdmin ? !!orgAdminRole : !!roleId && !!workspaceId);

    const runPreview = async () => {
        if (emails.length === 0) return;
        setPreviewing(true);
        try {
            const { data } = await iamApi.previewInvite({
                emails,
                workspaceId: asOrgAdmin ? undefined : workspaceId,
                roleId: asOrgAdmin ? orgAdminRole?._id : roleId,
                asOrgAdmin,
            });
            setPreview(data.results);
        } catch (e) {
            const err = e as { response?: { data?: { error?: string } }; message?: string };
            showErrorToast(err.response?.data?.error || err.message || 'Preview failed');
        } finally {
            setPreviewing(false);
        }
    };

    const handleSend = async () => {
        if (!canSubmit) return;
        setSending(true);
        const toSend = preview
            ? emails.filter((e) => {
                  const r = preview.find((p) => p.email === e);
                  return r?.status !== 'already_member';
              })
            : emails;

        if (toSend.length === 0) {
            showErrorToast('All listed emails are already members.');
            setSending(false);
            return;
        }

        const targetRoleId = asOrgAdmin ? orgAdminRole?._id : roleId;
        const targetWorkspaceId = asOrgAdmin ? undefined : workspaceId;

        let succeeded = 0;
        let failed = 0;
        for (const email of toSend) {
            try {
                await iamApi.sendInvite({ email, roleId: targetRoleId, workspaceId: targetWorkspaceId });
                succeeded++;
            } catch {
                failed++;
            }
        }

        setSending(false);
        if (succeeded > 0) showSuccessToast(`Sent ${succeeded} invite${succeeded === 1 ? '' : 's'}.`);
        if (failed > 0) showErrorToast(`${failed} invite${failed === 1 ? '' : 's'} failed.`);
        if (succeeded > 0) {
            onSent();
            onClose();
        }
    };

    const previewLabel = (status: InvitePreviewResult['status'], detail?: string) => {
        switch (status) {
            case 'new_user':
                return 'New user — will create account';
            case 'existing_in_org':
                return 'Existing user in this org — will add role';
            case 'existing_other_org':
                return 'Existing Lantern account in another org';
            case 'already_member':
                return detail || 'Already a member here';
            case 'already_pending':
                return 'Pending invite — will resend';
            default:
                return status;
        }
    };

    return (
        <Modal open={open} onClose={onClose} width={560}>
            <ModalHeader onClose={onClose}>Invite people</ModalHeader>
            <ModalBody>
                <label className="block text-xs font-medium text-gray-700 mb-1">Emails</label>
                <div className="border border-gray-300 rounded-md px-2 py-2 flex flex-wrap gap-1.5 min-h-[44px]">
                    {emails.map((e) => (
                        <span
                            key={e}
                            className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2 py-0.5 text-xs"
                        >
                            {e}
                            <button
                                onClick={() => removeEmail(e)}
                                className="text-gray-400 hover:text-gray-700"
                                aria-label={`Remove ${e}`}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                    <input
                        type="text"
                        value={emailDraft}
                        onChange={(e) => setEmailDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
                                e.preventDefault();
                                commitEmail();
                            } else if (e.key === 'Backspace' && !emailDraft && emails.length) {
                                setEmails((prev) => prev.slice(0, -1));
                            }
                        }}
                        onBlur={commitEmail}
                        placeholder={emails.length ? '' : 'name@example.com (press Enter or comma to add)'}
                        className="flex-1 min-w-[120px] outline-none text-sm"
                    />
                </div>

                <div className="mt-4 space-y-3">
                    {isOrgAdmin && orgAdminRole && (
                        <label className="flex items-center gap-2 text-sm text-gray-800">
                            <input
                                type="checkbox"
                                checked={asOrgAdmin}
                                onChange={(e) => {
                                    setAsOrgAdmin(e.target.checked);
                                    setPreview(null);
                                }}
                            />
                            Invite as organization admin (full access to every workspace)
                        </label>
                    )}

                    {!asOrgAdmin && (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Workspace</label>
                                <select
                                    value={workspaceId}
                                    onChange={(e) => {
                                        setWorkspaceId(e.target.value);
                                        setPreview(null);
                                    }}
                                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    disabled={!isOrgAdmin}
                                >
                                    {(isOrgAdmin ? workspaces : workspaces.filter((w) => w._id === currentWorkspaceId)).map((w) => (
                                        <option key={w._id} value={w._id}>
                                            {w.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                                <select
                                    value={roleId}
                                    onChange={(e) => {
                                        setRoleId(e.target.value);
                                        setPreview(null);
                                    }}
                                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                >
                                    <option value="">Select a role</option>
                                    {wsRoles.map((r) => (
                                        <option key={r._id} value={r._id}>
                                            {r.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                {emails.length > 0 && (
                    <div className="mt-4 flex items-center justify-between">
                        <button
                            type="button"
                            onClick={runPreview}
                            disabled={previewing || !canSubmit}
                            className="text-xs text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                        >
                            {previewing ? 'Checking…' : 'Preview before sending'}
                        </button>
                        {preview && (
                            <span className="text-xs text-gray-500">
                                {preview.filter((p) => p.status !== 'already_member').length} will be sent
                            </span>
                        )}
                    </div>
                )}

                {preview && (
                    <div className="mt-2 border rounded-md divide-y divide-gray-100 max-h-44 overflow-y-auto">
                        {preview.map((p) => (
                            <div key={p.email} className="flex items-center justify-between px-3 py-1.5 text-xs">
                                <span className="text-gray-800">{p.email}</span>
                                <span
                                    className={
                                        p.status === 'already_member'
                                            ? 'text-amber-600'
                                            : p.status === 'already_pending'
                                              ? 'text-amber-500'
                                              : 'text-gray-500'
                                    }
                                >
                                    {previewLabel(p.status, p.detail)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button variant="secondary" onClick={onClose} disabled={sending}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSend} disabled={!canSubmit || sending}>
                    {sending ? 'Sending…' : `Send invite${emails.length > 1 ? 's' : ''}`}
                </Button>
            </ModalFooter>
        </Modal>
    );
}
